import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

/**
 * VancouverCherryBlossomsArticle
 * 
 * A comprehensive guide to Vancouver's cherry blossom season.
 */
export default function VancouverCherryBlossomsArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-cherry-blossoms";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "best-viewing-locations": "Best Viewing Locations",
    "self-guided-walking-route": "Self-Guided Walking Route",
    "festival-events": "Festival & Events",
    "photography-tips": "Photography Tips",
    "practical-tips": "Practical Tips",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vcbf.ca/",
      label: "Vancouver Cherry Blossom Festival",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture.aspx",
      label: "City of Vancouver: Parks & Recreation",
    },
    {
      url: "https://weather.gc.ca/",
      label: "Environment Canada Weather",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location’s "id" matches the ref you use in the corresponding text section.
  // Make sure "coordY" = lat, "coordX" = lng for the <Marker> component usage.
  const locations = [
    {
      id: "stanley-park",
      lat: 49.3043,
      lng: -123.1443,
      popupText: "Stanley Park (Cherry Blossoms)",
    },
    {
      id: "queen-elizabeth-park",
      lat: 49.2415,
      lng: -123.1127,
      popupText: "Queen Elizabeth Park (Cherry Blossoms)",
    },
    {
      id: "vandusen-garden",
      lat: 49.2395,
      lng: -123.1284,
      popupText: "VanDusen Botanical Garden (Cherry Blossoms)",
    },
    {
      id: "burrard-station",
      lat: 49.2840,
      lng: -123.1204,
      popupText: "Burrard Station / Vancouver Art Gallery Area",
    },
    {
      id: "arbutus",
      lat: 49.2509,
      lng: -123.1530,
      popupText: "West 22nd & Arbutus (Blossom Tunnel)",
    },
    {
      id: "commercial-drive",
      lat: 49.2633,
      lng: -123.0717,
      popupText: "East 10th Ave near Commercial Drive (Cherry Blossoms)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Cherry Blossoms in Bloom</title>
        <meta
          name="description"
          content="Discover the best cherry blossom viewing spots, festivals, and practical tips for enjoying Vancouver's iconic spring blooms."
        />
        <meta
          name="keywords"
          content="vancouver, cherry blossoms, travel, city parks, photography"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-cherry-blossoms.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Cherry Blossoms in Bloom"
        />
        <meta
          property="og:description"
          content="A complete guide to Vancouver's cherry blossom season, featuring top locations, a self-guided route, festival info, and photography tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-cherry-blossoms.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-cherry-blossoms.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Cherry Blossoms in Bloom"
        />
        <meta
          name="twitter:description"
          content="A must-read guide to Vancouver's cherry blossoms, including best spots and festival details."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-cherry-blossoms.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={ref_data?.["image-desc"] || "Vancouver Cherry Blossoms"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Each spring, Vancouver’s streets and parks transform into a sea
              of pink and white blossoms, thanks to over 40,000 cherry trees
              planted throughout the city. Peak bloom typically occurs between
              late March and mid-April, offering a brief but breathtaking
              spectacle for residents and visitors alike. This article covers
              the best locations to see the blossoms, a self-guided route,
              festival highlights, photography tips, and more.
            </p>

            {/* SECTION: Best Viewing Locations */}
            <h2 id="best-viewing-locations">Best Viewing Locations</h2>
            <p>
              Vancouver boasts numerous “must-see” blossom spots. Here are some
              top picks:
            </p>
            <ul>
              <li
                ref={(el) => setSpotRef("stanley-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Stanley Park (49.3043, -123.1443)</strong>{" "}
                <Marker
                  coordY={49.3043}
                  coordX={-123.1443}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . The city’s famous urban park features clusters of cherry trees
                near the main entrance and around the formal gardens. A walk or
                bike ride along the seawall is an ideal way to soak in the
                springtime ambiance.
              </li>
              <li
                ref={(el) => setSpotRef("queen-elizabeth-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Queen Elizabeth Park (49.2415, -123.1127)</strong>{" "}
                <Marker
                  coordY={49.2415}
                  coordX={-123.1127}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . At the highest point in Vancouver, you’ll enjoy panoramic
                city views alongside vibrant cherry blossoms. The quarry gardens
                make for spectacular photo backdrops.
              </li>
              <li
                ref={(el) => setSpotRef("vandusen-garden", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>VanDusen Botanical Garden (49.2395, -123.1284)</strong>{" "}
                <Marker
                  coordY={49.2395}
                  coordX={-123.1284}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . This paid-entry garden offers meticulously curated spaces
                showcasing diverse flora. Cherry trees here often reach full
                bloom in April; check the garden’s website for updates on
                current conditions.
              </li>
              <li
                ref={(el) => setSpotRef("burrard-station", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>
                  Burrard Station / Vancouver Art Gallery Area (49.2840,
                  -123.1204)
                </strong>{" "}
                <Marker
                  coordY={49.2840}
                  coordX={-123.1204}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . Downtown explorers will find blossoming trees lining Burrard
                Street, Robson Square, and around the Vancouver Art Gallery.
              </li>
              <li
                ref={(el) => setSpotRef("arbutus", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>
                  West 22nd & Arbutus (49.2509, -123.1530)
                </strong>{" "}
                <Marker
                  coordY={49.2509}
                  coordX={-123.1530}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . Residential streets in this area form picturesque “tunnels” of
                blossoms—ideal for dreamy photo shoots.
              </li>
              <li
                ref={(el) => setSpotRef("commercial-drive", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>
                  East 10th Ave near Commercial Drive (49.2633, -123.0717)
                </strong>{" "}
                <Marker
                  coordY={49.2633}
                  coordX={-123.0717}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                . Known for its eclectic cafés and shops, this lively
                neighborhood also enjoys bursts of pink blossoms on side
                streets.
              </li>
            </ul>

            {/* SECTION: Self-Guided Walking Route */}
            <h2 id="self-guided-walking-route">Self-Guided Walking Route</h2>
            <p>
              A great way to experience multiple bloom hotspots is on foot. 
              Here’s a suggested route, roughly 2–3 hours at a leisurely pace:
            </p>
            <ol>
              <li>
                **Start at** <strong>Burrard Station</strong> and wander along
                Robson Street to enjoy city blossoms.
              </li>
              <li>
                **Continue** to David Lam Park for waterfront views in Yaletown,
                then follow the seawall to Olympic Village.
              </li>
              <li>
                **End near** <strong>Science World</strong>, with the option to
                catch the SkyTrain at Main Street–Science World Station back
                downtown.
              </li>
            </ol>

            {/* SECTION: Festival & Events */}
            <h2 id="festival-events">Festival & Events</h2>
            <p>
              The Vancouver Cherry Blossom Festival (<a href="https://vcbf.ca/" target="_blank" rel="noreferrer">VCBF</a>) occurs each spring
              with a variety of events:
            </p>
            <ul>
              <li>
                **Cherry Jam Downtown**: Musical performances, often hosted near
                Burrard Station or other central locations.
              </li>
              <li>
                **Sakura Days Japan Fair**: A cultural celebration at VanDusen
                Botanical Garden featuring tea ceremonies, artisan booths, and
                more.
              </li>
              <li>
                **Haiku Invitational**: A creative competition welcoming poetry
                from around the globe.
              </li>
            </ul>
            <p>
              For the latest schedule and ticket info, visit{" "}
              <a href="https://vcbf.ca/" target="_blank" rel="noreferrer">
                vcbf.ca
              </a>
              .
            </p>

            {/* SECTION: Photography Tips */}
            <h2 id="photography-tips">Photography Tips</h2>
            <ul>
              <li>
                **Timing**: Early morning or late afternoon (golden hour)
                provides softer light and fewer crowds.
              </li>
              <li>
                **Angles**: Look for “tunnel” views or shoot upward to capture
                bright blossoms contrasted against the sky.
              </li>
              <li>
                **Overcast Advantage**: Vancouver’s cloudy spring days often
                help by diffusing direct sunlight, resulting in pleasing, even
                lighting for blossom photography.
              </li>
            </ul>

            {/* SECTION: Practical Tips */}
            <h2 id="practical-tips">Practical Tips</h2>
            <ul>
              <li>
                **Public Transit**: Use{" "}
                <a href="https://www.translink.ca/" target="_blank" rel="noreferrer">
                  TransLink
                </a>{" "}
                for convenient city-wide travel; many cherry blossom spots are
                accessible via bus or SkyTrain.
              </li>
              <li>
                **Parking**: Street parking can be limited, particularly near
                downtown or popular parks. Paid parking is available at major
                destinations.
              </li>
              <li>
                **Weather Preparedness**: Vancouver’s spring weather can be
                unpredictable; dress in layers and keep an umbrella handy.
              </li>
              <li>
                **Bloom Tracking**: Check the{" "}
                <a
                  href="https://vcbf.ca/where-to-view/"
                  target="_blank"
                  rel="noreferrer"
                >
                  VCBF bloom map
                </a>{" "}
                for real-time updates on peak spots.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to see the blossoms?</h3>
              <p>
                Typically, peak bloom hits around early April. Warmer or cooler
                weather can shift this window slightly each year.
              </p>

              <h3>2. Are festival events free?</h3>
              <p>
                Many festival activities, like Cherry Jam Downtown, are free.
                However, events at venues like VanDusen might require an
                admission fee. Always confirm details on the official festival
                website.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              When photographing blossoms along streets, be mindful of traffic
              and driveways. Do not climb or damage the cherry trees. Follow any
              posted signage about restricted parking or tree protection
              bylaws. In parks, observe guidelines that protect the natural
              environment.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s cherry blossom season is a brief but spectacular
              celebration of spring. Whether you stroll through Stanley Park,
              wander residential streets beneath pink canopies, or attend a
              festival event, you’ll find that these blossoms create a vivid
              sense of renewal. Plan your route, check bloom updates, and enjoy
              this fleeting marvel of nature.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}